import { forwardRef, useEffect, useRef, useState, useCallback } from "react";

const MaskedImageFadeReveal = forwardRef(({ hoverimage }, ref) => {
  const maskRef = useRef(null);
  const brushUrl =
    "https://studiomaamangala.com/wp-content/uploads/2025/06/camera-view.png";

  const targetPos = useRef({ x: 0, y: 0 });
  const currentPos = useRef({ x: 0, y: 0 });
  const lastMousePos = useRef({ x: 0, y: 0 });
  const opacity = useRef(0);
  const animationFrame = useRef();

  const brushSize = 300;

  const [isShaking, setIsShaking] = useState(false);
  const shakeTimeout = useRef();

  // Wrap animate in useCallback
  const animate = useCallback(() => {
    currentPos.current.x += (targetPos.current.x - currentPos.current.x) * 0.2;
    currentPos.current.y += (targetPos.current.y - currentPos.current.y) * 0.2;

    const jitterX = (Math.random() - 0.5) * 6;
    const jitterY = (Math.random() - 0.5) * 6;

    const offsetX = currentPos.current.x - brushSize / 2 + jitterX;
    const offsetY = currentPos.current.y - brushSize / 2 + jitterY;

    if (maskRef.current) {
      maskRef.current.style.maskPosition = `${offsetX}px ${offsetY}px`;
      maskRef.current.style.WebkitMaskPosition = `${offsetX}px ${offsetY}px`;

      opacity.current += (0 - opacity.current) * 0.3;

      const flicker = Math.random() * 0.02;
      maskRef.current.style.opacity = Math.min(1, opacity.current + flicker);
    }

    animationFrame.current = requestAnimationFrame(animate);
  }, [brushSize]);

  // Wrap handleMouseMove in useCallback
  const handleMouseMove = useCallback(
    (e) => {
      if (!ref?.current) return;

      const rect = ref.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const dx = x - lastMousePos.current.x;
      const dy = y - lastMousePos.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy);

      const mappedOpacity = Math.min(1, speed / 2);
      opacity.current = mappedOpacity;

      lastMousePos.current = { x, y };
      targetPos.current = { x, y };

      if (!isShaking) {
        setIsShaking(true);
        clearTimeout(shakeTimeout.current);
        shakeTimeout.current = setTimeout(() => {
          setIsShaking(false);
        }, 300);
      } else {
        clearTimeout(shakeTimeout.current);
        shakeTimeout.current = setTimeout(() => {
          setIsShaking(false);
        }, 300);
      }
    },
    [isShaking, ref]
  );

  useEffect(() => {
    if (!ref?.current) return;

    const element = ref.current;
    element.addEventListener("mousemove", handleMouseMove);
    animationFrame.current = requestAnimationFrame(animate);

    return () => {
      element.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrame.current);
      clearTimeout(shakeTimeout.current);
    };
  }, [ref, animate, handleMouseMove]);

  return (
    <>
      <div
        ref={ref}
        className={isShaking ? "camera-shake" : ""}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <div
          ref={maskRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundImage: `url(${hoverimage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.1)",

            maskImage: `url(${brushUrl})`,
            WebkitMaskImage: `url(${brushUrl})`,
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskSize: `${brushSize}px`,
            WebkitMaskSize: `${brushSize}px`,

            maskPosition: `-9999px -9999px`,
            WebkitMaskPosition: `-9999px -9999px`,

            opacity: 0,
            transition: "opacity 0.15s ease-out",

            animation: "cyberGlitch 0.4s infinite steps(3, start)",
          }}
        />
      </div>

      <style>
        {`
          @keyframes cyberGlitch {
            0%   { transform: translate(0px, 0px) scale(1); }
            10%  { transform: translate(3px, -3px) scale(1.02) rotate(1deg); }
            20%  { transform: translate(-3px, 3px) scale(1.04) rotate(-1.2deg); }
            30%  { transform: translate(2px, -2px) scale(1.01) rotate(0.8deg); }
            40%  { transform: translate(-2px, 2px) scale(1.03) rotate(-0.7deg); }
            50%  { transform: translate(0px, 0px) scale(1); }
            60%  { transform: translate(1px, -1px) scale(1.02) rotate(0.5deg); }
            70%  { transform: translate(-1px, 1px) scale(1.03) rotate(-0.6deg); }
            80%  { transform: translate(2px, -2px) scale(1.01); }
            90%  { transform: translate(-2px, 2px) scale(1.02) rotate(0.4deg); }
            100% { transform: translate(0px, 0px) scale(1); }
          }

          @keyframes cameraShake {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            10%, 30%, 50%, 70%, 90% { transform: translate(-3px, 3px) rotate(-0.5deg); }
            20%, 40%, 60%, 80% { transform: translate(3px, -3px) rotate(0.5deg); }
          }

          .camera-shake {
            animation: cameraShake 0.3s ease-in-out;
          }
        `}
      </style>
    </>
  );
});

export default MaskedImageFadeReveal;
